import { z } from 'zod';

/**
 * Bentuk baku kolom `Product.specs`.
 *
 * `rows` adalah tabel spesifikasi key-value yang tampil di panel produk;
 * `highlights` adalah poin singkat yang sudah dipakai lebih dulu oleh penulis
 * deskripsi AI. Keduanya sengaja dalam satu kolom Json supaya tidak menambah
 * tabel untuk data yang selalu dibaca bersama produknya.
 */
export const productSpecRowSchema = z.object({
  label: z.string().trim().min(1, 'Label tidak boleh kosong').max(60),
  value: z.string().trim().min(1, 'Isi tidak boleh kosong').max(200),
});

export const productSpecsSchema = z.object({
  rows: z.array(productSpecRowSchema).max(30, 'Maksimal 30 baris spesifikasi').default([]),
  highlights: z.array(z.string().trim().min(1).max(200)).max(10).default([]),
});

export type ProductSpecRow = z.infer<typeof productSpecRowSchema>;
export type ProductSpecs = z.infer<typeof productSpecsSchema>;

export const EMPTY_PRODUCT_SPECS: ProductSpecs = { rows: [], highlights: [] };

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readRows(value: unknown): ProductSpecRow[] {
  if (!Array.isArray(value)) return [];
  const rows: ProductSpecRow[] = [];
  for (const entry of value) {
    const parsed = productSpecRowSchema.safeParse(entry);
    if (parsed.success) rows.push(parsed.data);
    if (rows.length === 30) break;
  }
  return rows;
}

function readHighlights(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry): entry is string => typeof entry === 'string' && entry.trim() !== '')
    .map((entry) => entry.trim().slice(0, 200))
    .slice(0, 10);
}

/**
 * Membaca `specs` apa adanya dari database tanpa pernah melempar.
 *
 * Kolom ini sudah berisi data lama dengan bentuk bebas (`{}`, `{highlights: []}`,
 * atau kunci karangan seperti `{mesin: "..."}`), dan halaman produk tidak boleh
 * gagal render karena satu baris yang bentuknya tidak terduga. Yang tidak
 * dikenali diabaikan, bukan dipaksa masuk.
 */
export function readProductSpecs(value: unknown): ProductSpecs {
  const record = asRecord(value);
  if (!record) return EMPTY_PRODUCT_SPECS;
  return {
    rows: readRows(record.rows),
    highlights: readHighlights(record.highlights),
  };
}

/** true bila tidak ada yang bisa ditampilkan dari specs. */
export function isProductSpecsEmpty(specs: ProductSpecs): boolean {
  return specs.rows.length === 0 && specs.highlights.length === 0;
}
